import React from 'react';
import { isAfter, startOfToday, startOfYesterday, startOfWeek, startOfMonth } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import { getCollectionDetailsPageUrl } from '@jira/polaris__component-collections/src/getCollectionsPageUrl.tsx';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import PagesIcon from '@atlaskit/icon/core/pages';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import { getEmojiProviderWithCustomEmoji } from '@jira/platform__atlaskit-emoji-provider/src/emoji.tsx';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { ContainerAvatar } from '@atlaskit/side-nav-items/container-avatar';
import { formatDistanceStrictWithLocale } from '@jira/platform__utils-date-fns/src/main.tsx';
import { softwarePlanTimelineRoute } from '@jira/router-routes__advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { generatePath } from '@atlassian/react-resource-router';
import {
	BOARD,
	JPD_COLLECTION,
	DASHBOARD,
	FILTER,
	ISSUE,
	PLAN,
	PROJECT,
	QUEUE,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';

import type { ObjectListFlyoutHookResults } from '@atlassian/navigation-common/types';
import { defineMessage, type IntlShape } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraProductDiscoveryCollectionAri } from '@atlassian/ari/jira-product-discovery/collection';
import type { useRecentItemsPaged$data } from './__generated__/useRecentItemsPaged.graphql.ts';
import type { JiraRecentOLFItem, TimeRanges } from './JiraRecentOLFItem.types.tsx';
import { EmojiItem } from './EmojiItem.tsx';

const timeAgoMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.time-ago',

	defaultMessage: '{timeAgo} ago',
	description:
		"Label telling user that jira item was last accessed 'timeAgo' ago (such as 5 hours ago)",
});

const todayMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.today-group',

	defaultMessage: 'Today',
	description: 'Label for the group of items that were accessed today',
});

const yesterdayMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.yesterday-group',

	defaultMessage: 'Yesterday',
	description: 'Label for the group of items that were accessed yesterday',
});

const thisWeekMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.this-week-group',

	defaultMessage: 'This week',
	description: 'Label for the group of items that were accessed this week',
});

const thisMonthMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.this-month-group',

	defaultMessage: 'This month',
	description: 'Label for the group of items that were accessed this month',
});

const moreThanAMonthMessage = defineMessage({
	id: 'nav-platform-integration.sidebar.common.ui.recent.more-than-a-month-group',

	defaultMessage: 'More than a month',
	description: 'Label for the group of items that were accessed more than a month ago',
});

const messages = {
	timeAgo: timeAgoMessage,
	today: todayMessage,
	yesterday: yesterdayMessage,
	thisWeek: thisWeekMessage,
	thisMonth: thisMonthMessage,
	moreThanAMonth: moreThanAMonthMessage,
};

type NonNullableEdges = NonNullable<useRecentItemsPaged$data['jira']['_recentItems']>['edges'];
type Edge = NonNullable<NonNullableEdges>[number];
export type GQLJiraItem = NonNullable<NonNullable<Edge>['node']>;
type GroupedItems = Record<TimeRanges, GQLJiraItem[]>;

type TimeZone = string;
export const getSections: MemoizedFn<
	(
		items: GQLJiraItem[],
		timeZone: TimeZone,
		deps: Deps,
	) => ObjectListFlyoutHookResults<JiraRecentOLFItem>['sections']
> = memoizeOne(getSectionItems);

function getSectionItems(
	items: GQLJiraItem[],
	timeZone: TimeZone,
	deps: Deps,
): ObjectListFlyoutHookResults<JiraRecentOLFItem>['sections'] {
	const now = getCurrentTime();

	const groups = items.reduce<GroupedItems>(
		(acc, item) => {
			const viewTimeAgo = getLastViewedTimestamp(item);

			if (viewTimeAgo) {
				const timeAccessed = new Date(viewTimeAgo);
				const dateObj = utcToZonedTime(timeAccessed, timeZone);

				if (isAfter(dateObj, startOfToday())) {
					acc.today.push(item);
				} else if (isAfter(dateObj, startOfYesterday())) {
					acc.yesterday.push(item);
				} else if (isAfter(dateObj, startOfWeek(now))) {
					acc.thisWeek.push(item);
				} else if (isAfter(dateObj, startOfMonth(now))) {
					acc.thisMonth.push(item);
				} else {
					acc.moreThanAMonth.push(item);
				}
			}

			return acc;
		},
		{
			today: [],
			yesterday: [],
			thisWeek: [],
			thisMonth: [],
			moreThanAMonth: [],
		},
	);

	// oxlint-disable-next-line typescript/consistent-type-assertions
	return (Object.entries(groups) as unknown as [TimeRanges, GQLJiraItem[]][]).map(
		([key, value]) => ({
			header: deps.formatMessage(messages[key]),
			items: value.map((i) => toOLFItem(i, key, deps)).filter(Boolean),
		}),
	);
}

export function getCurrentTime(): number {
	return Date.now();
}

function getLastViewedTimestamp(item: GQLJiraItem) {
	switch (item.__typename) {
		case 'JiraIssue':
		case 'JiraProject':
		case 'JiraPlan':
		case 'JiraBoard':
		case 'JiraDashboard':
		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
		case 'JiraServiceManagementQueue':
			return item.lastViewedTimestamp;
		case 'JiraProductDiscoveryCollection':
			return fg('jpd-enable-roadmaps-recents-fe') ? item.lastViewedTimestamp : null;
		default:
			return null;
	}
}

function toOLFItem(
	item: GQLJiraItem,
	timeRange: TimeRanges,
	deps: Deps,
): JiraRecentOLFItem | undefined {
	switch (item.__typename) {
		case 'JiraBoard': {
			const isLocationless = item.boardUrl?.includes('locationless=true');

			return isLocationless
				? undefined
				: {
						elemBefore: <BoardIcon spacing="spacious" color="currentColor" label="" />,
						href: item.boardUrl ?? '',
						primaryLabel: String(item.name ?? ''),
						secondaryLabel: getLastViewedDescription({
							lastViewedTimestamp: item.lastViewedTimestamp,
							...deps,
						}),
						analytics: { itemId: String(item?.boardId), itemType: BOARD, timeRange },
					};
		}

		case 'JiraDashboard':
			return {
				elemBefore: <DashboardIcon spacing="spacious" color="currentColor" label="" />,
				href: item.dashboardUrl ?? '',
				primaryLabel: String(item.dashboardTitle ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.dashboardId), itemType: DASHBOARD, timeRange },
			};

		case 'JiraIssue':
			return {
				elemBefore: <WorkItemIcon spacing="spacious" color="currentColor" label="" />,
				href: item.issueWebUrl ?? '',
				primaryLabel: String(item.summaryField?.text ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.issueId), itemType: ISSUE, timeRange },
			};

		case 'JiraPlan': {
			const planId = item.planId;
			const scenarioId = item.scenario?.scenarioId ?? '';
			const planUrl = generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });

			return {
				elemBefore: <RoadmapIcon spacing="spacious" color="currentColor" label="" />,
				href: planUrl,
				primaryLabel: String(item.planTitle ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.planId), itemType: PLAN, timeRange },
			};
		}

		case 'JiraProject':
			return {
				elemBefore: <ContainerAvatar src={item.avatar?.large ?? ''} />,
				href: item.defaultNavigationItem?.url ?? `/browse/${item.key}`,
				primaryLabel: item.title,
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.projectId), itemType: PROJECT, timeRange },
			};

		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
			return {
				elemBefore: <FilterIcon spacing="spacious" color="currentColor" label="" />,
				href: item.filterUrl ?? '',
				primaryLabel: item.filterName,
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.filterId), itemType: FILTER, timeRange },
			};

		case 'JiraServiceManagementQueue':
			return {
				elemBefore: <PagesIcon spacing="spacious" color="currentColor" label="" />,
				href: item.queueUrl ?? '',
				primaryLabel: String(item.queueTitle || ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.queueId), itemType: QUEUE, timeRange },
			};
		case 'JiraProductDiscoveryCollection': {
			if (!fg('jpd-enable-roadmaps-recents-fe')) {
				return undefined;
			}
			const uuid = item.id ? JiraProductDiscoveryCollectionAri.parse(item.id).collectionId : '';
			const collectionUrl = getCollectionDetailsPageUrl(uuid);
			const fallback = <PlanIcon spacing="spacious" color="currentColor" label="" />;

			return {
				elemBefore: (
					<EmojiItem
						emoji={item.emoji}
						getEmojiProvider={getEmojiProviderWithCustomEmoji}
						cloudId={deps.cloudId}
						accountId={deps.accountId}
						fallback={fallback}
					/>
				),
				href: collectionUrl || item.collectionUrl || '',
				primaryLabel: String(item.collectionTitle ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.collectionId), itemType: JPD_COLLECTION, timeRange },
			};
		}
		default:
			return undefined;
	}
}

type Deps = {
	formatMessage: IntlShape['formatMessage'];
	locale: Locale;
	cloudId?: string;
	accountId?: string;
};

type GetLastViewedDescriptionParams = Deps & {
	lastViewedTimestamp: number | null | undefined;
	description?: string;
};

// Non-hook version of src/packages/navigation-apps/sidebar-nav4-sidebars-content-recent/src/common/controllers/index.tsx
// Exported for testing
export function getLastViewedDescription(params: GetLastViewedDescriptionParams): string {
	const { description, formatMessage, lastViewedTimestamp, locale } = params;
	const now = getCurrentTime();

	const lastViewedTimeText = lastViewedTimestamp
		? formatMessage(messages.timeAgo, {
				timeAgo: formatDistanceStrictWithLocale(lastViewedTimestamp, now, locale),
			})
		: '';
	if ((description?.length ?? 0) > 0 && lastViewedTimeText.length > 0) {
		return `${description} \u2022 ${lastViewedTimeText}`;
	}
	return lastViewedTimeText;
}
